import { useState } from "react";
import { toast } from "sonner";
import { Save, Trash2 } from "lucide-react";
import { exportApi } from "@/lib/api";

export function TemplateBar({ presets, templates, onApply, onSaved, selected, status }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState("");

  const all = [...presets, ...templates];
  const apply = (id) => {
    setCurrent(id);
    const t = all.find((x) => x.id === id);
    if (t) onApply(t);
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Give your template a name"); return; }
    if (!selected.size) { toast.error("Select at least one field"); return; }
    setSaving(true);
    try {
      await exportApi.saveTemplate({ name, keys: [...selected], status });
      toast.success("Template saved");
      setName("");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save template");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const t = templates.find((x) => x.id === current);
    if (!window.confirm(`Delete template "${t?.name}"?`)) return;
    try {
      await exportApi.deleteTemplate(current);
      toast.success("Template deleted");
      setCurrent("");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete template");
    }
  };

  const isMine = templates.some((t) => t.id === current);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select value={current} onChange={(e) => apply(e.target.value)} data-testid="export-template-select" className="rounded-xl border border-border bg-background px-3 py-2 text-sm font-medium">
        <option value="">Choose a template…</option>
        <optgroup label="Ready-made">{presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>
        {templates.length > 0 && <optgroup label="My templates">{templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</optgroup>}
      </select>
      {isMine && (
        <button onClick={remove} data-testid="export-template-delete" title="Delete template" className="rounded-xl border border-border p-2 text-destructive transition-colors hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
      )}
      <div className="ml-auto flex items-center gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Save selection as…" data-testid="export-template-name-input" className="w-44 rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        <button onClick={save} disabled={saving} data-testid="export-template-save" className="inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-2 text-sm font-semibold transition-colors hover:bg-secondary disabled:opacity-60">
          <Save className="h-4 w-4" /> Save
        </button>
      </div>
    </div>
  );
}
